import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, Send, Plus, CheckCircle2, Circle, Loader, Banknote, FolderArchive, ExternalLink, UserPlus, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import PortalNav from "@/components/PortalNav";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const MS_FLOW = ["pending", "in_progress", "approved", "paid"];
const MS_META = {
  pending: { label: "Pending", cls: "text-zinc-400 border-white/15 bg-white/5", icon: Circle },
  in_progress: { label: "In Progress", cls: "text-nc-blue border-nc-blue/40 bg-nc-blue/10", icon: Loader },
  approved: { label: "Approved", cls: "text-nc-orange border-nc-orange/40 bg-nc-orange/10", icon: CheckCircle2 },
  paid: { label: "Paid", cls: "text-nc-green border-nc-green/40 bg-nc-green/10", icon: Banknote },
};

function MilestoneTimeline({ milestones, me, isAssignedDev, isClient, onAction, onDeposit, depositReceived }) {
  const { fmt, currency } = useCurrency();
  return (
    <div data-testid="milestone-timeline" className="flex flex-col">
      {milestones.map((m, i) => {
        const meta = MS_META[m.status] || MS_META.pending;
        const stepIdx = MS_FLOW.indexOf(m.status);
        return (
          <div key={m.id} data-testid={`milestone-${i + 1}`} className="flex gap-4">
            {/* rail */}
            <div className="flex flex-col items-center">
              <span className={`w-8 h-8 border flex items-center justify-center shrink-0 ${meta.cls}`}><meta.icon className="w-4 h-4" /></span>
              {i < milestones.length - 1 && <span className={`w-px flex-1 ${stepIdx > 0 ? "bg-nc-blue/50" : "bg-white/10"}`} />}
            </div>
            {/* body */}
            <div className="pb-6 min-w-0 flex-1">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="font-sans font-semibold text-sm text-zinc-100">{m.order}. {m.title}</p>
                <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-0.5 ${meta.cls}`}>{meta.label}</span>
              </div>
              {m.description && <p className="font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed">{m.description}</p>}
              <p className="font-mono text-[10px] text-zinc-400 mt-1">
                <span className="text-nc-green">{fmt(Number(m.price))} {currency}</span>
                {m.due_date && <span className="text-zinc-600"> // due {m.due_date}</span>}
              </p>
              <div className="flex gap-2 mt-2">
                {m.status === "pending" && isClient && !depositReceived && i === 0 && (
                  <button
                    data-testid={`milestone-deposit-${i + 1}`}
                    onClick={() => onDeposit(m.id)}
                    className="font-mono text-[8px] tracking-[0.15em] uppercase px-2.5 py-1.5 bg-nc-green hover:bg-nc-green/80 text-white transition-colors"
                  >
                    Pay Deposit via Stripe — {fmt(Number(m.price))} {currency}
                  </button>
                )}
                {m.status === "pending" && (isAssignedDev || me.role === "admin") && (
                  <button data-testid={`milestone-start-${i + 1}`} onClick={() => onAction(m.id, "in_progress")} className="font-mono text-[8px] tracking-[0.15em] uppercase px-2.5 py-1.5 border border-nc-blue/40 text-nc-blue hover:bg-nc-blue/10 transition-colors">Start Work</button>
                )}
                {m.status === "in_progress" && (isClient || me.role === "admin") && (
                  <button data-testid={`milestone-approve-${i + 1}`} onClick={() => onAction(m.id, "approved")} className="font-mono text-[8px] tracking-[0.15em] uppercase px-2.5 py-1.5 border border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10 transition-colors">Approve Delivery</button>
                )}
                {m.status === "approved" && me.role === "admin" && (
                  <button data-testid={`milestone-pay-${i + 1}`} onClick={() => onAction(m.id, "paid")} className="font-mono text-[8px] tracking-[0.15em] uppercase px-2.5 py-1.5 bg-nc-green hover:bg-nc-green/80 text-white transition-colors">Mark Paid</button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function PortalPage() {
  const navigate = useNavigate();
  const { fmt, currency } = useCurrency();
  const [me, setMe] = useState(null);
  const [projects, setProjects] = useState([]);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [devs, setDevs] = useState([]);
  const [assignTarget, setAssignTarget] = useState("");
  const [deliv, setDeliv] = useState({ title: "", file_url: "", version: "1.0", notes: "" });
  const [addingDeliv, setAddingDeliv] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => { localStorage.removeItem("nc_token"); navigate("/auth"); });
  }, [navigate]);

  const loadProjects = useCallback(() => {
    api.get("/portal/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const loadDetail = useCallback(async (pid) => {
    try {
      const r = await api.get(`/portal/projects/${pid}`);
      setDetail(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (me) loadProjects(); }, [me, loadProjects]);
  useEffect(() => {
    if (me?.role === "admin") api.get("/users").then((r) => setDevs(r.data.filter((u) => u.role === "dev"))).catch(() => {});
  }, [me]);

  const pollChat = useCallback(async () => {
    if (!detail?.project?.channel_id) return;
    try {
      const r = await api.get(`/sales/chat/${detail.project.channel_id}`);
      setMessages(r.data.messages);
    } catch { /* ignore */ }
  }, [detail]);

  useEffect(() => {
    if (!detail) return undefined;
    pollChat();
    const id = setInterval(() => { pollChat(); loadDetail(detail.project.id); }, 4000);
    return () => clearInterval(id);
  }, [pollChat, detail, loadDetail]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (!me) return <div className="min-h-screen bg-[#050505]" />;

  const isClient = detail && detail.project.client_id === me.id;
  const isAssignedDev = detail && (me.role === "admin" || detail.assignments.some((a) => a.dev_id === me.id));

  const milestoneAction = async (mid, status) => {
    try {
      await api.patch(`/portal/milestones/${mid}`, { status });
      toast.success(`Milestone ${status.replace("_", " ")}`);
      loadDetail(detail.project.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const payDeposit = async (mid) => {
    try {
      const r = await api.post(`/portal/milestones/${mid}/deposit-checkout`, { origin_url: window.location.origin, currency });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const send = async () => {
    if (!draft.trim()) return;
    try {
      await api.post(`/sales/chat/${detail.project.channel_id}`, { text: draft.trim(), guest_name: "" });
      setDraft("");
      pollChat();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const assignDev = async () => {
    if (!assignTarget) return;
    try {
      await api.post(`/admin/projects/${detail.project.id}/assign`, { dev_id: assignTarget });
      toast.success("Developer assigned");
      setAssignTarget("");
      loadDetail(detail.project.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unassignDev = async (devId) => {
    try {
      await api.post(`/admin/projects/${detail.project.id}/unassign`, { dev_id: devId });
      toast.success("Developer removed");
      loadDetail(detail.project.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addDeliverable = async () => {
    try {
      await api.post(`/portal/projects/${detail.project.id}/deliverables`, deliv);
      toast.success("Deliverable added to the vault");
      setDeliv({ title: "", file_url: "", version: "1.0", notes: "" });
      setAddingDeliv(false);
      loadDetail(detail.project.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const paidTotal = detail ? detail.milestones.filter((m) => m.status === "paid").reduce((s, m) => s + m.price, 0) : 0;

  return (
    <div data-testid="portal-page" className="min-h-screen bg-[#050505] text-zinc-100 pt-6 pb-16 px-4 sm:px-6">
      <div className="max-w-[1400px] mx-auto flex flex-col gap-5">
        <PortalNav title="Client Workspace" />
        <div>
          <p className="font-mono text-[9px] tracking-[0.35em] text-nc-blue uppercase">Specpixel // Client Operating System</p>
          <h1 className="font-sans font-bold text-2xl uppercase tracking-wider mt-1 flex items-center gap-3"><Briefcase className="w-6 h-6 text-nc-blue" /> Client Workspace</h1>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[300px_1fr] gap-5 items-start">
          {/* Projects list */}
          <div className="border border-white/10 bg-black/40">
            <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase">Projects ({projects.length})</div>
            {projects.length === 0 && (
              <div className="p-4">
                <p data-testid="projects-empty" className="font-mono text-[10px] text-zinc-600 leading-relaxed">
                  No active projects on this account. Open the project uplink on the site to start a conversation with the studio.
                </p>
              </div>
            )}
            {projects.map((p, i) => (
              <div key={p.id} data-testid={`project-row-${i + 1}`} onClick={() => loadDetail(p.id)} className={`px-4 py-3 border-b border-white/5 cursor-pointer transition-colors ${detail?.project?.id === p.id ? "bg-nc-blue/10 border-l-2 border-l-nc-blue" : "hover:bg-white/5"}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-sans font-semibold text-sm text-zinc-100 truncate">{p.title}</p>
                  <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-1.5 py-0.5 shrink-0 ${p.status === "active" ? "text-nc-green border-nc-green/40 bg-nc-green/10" : "text-zinc-400 border-white/15"}`}>{p.status}</span>
                </div>
                <p className="font-mono text-[9px] text-zinc-500 mt-0.5">{p.engine_type} // {fmt(Number(p.budget_total))}</p>
              </div>
            ))}
          </div>

          {/* Detail */}
          {detail ? (
            <div className="flex flex-col gap-5 min-w-0">
              {/* Header */}
              <div className="border border-white/10 bg-black/40 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h2 data-testid="project-title" className="font-sans font-bold text-lg text-zinc-50">{detail.project.title}</h2>
                    <p className="font-mono text-[10px] text-zinc-500 mt-1">
                      {detail.project.engine_type} // client {detail.project.client_email} // rep {detail.project.sales_rep_email}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase">Budget</p>
                    <p data-testid="project-budget" className="font-sans font-bold text-xl text-nc-green">{fmt(Number(detail.project.budget_total))}</p>
                    <p className="font-mono text-[9px] text-zinc-600">{fmt(paidTotal)} paid out</p>
                  </div>
                </div>
                {/* Admin: dev assignment */}
                {me.role === "admin" && (
                  <div className="border-t border-white/10 mt-4 pt-3 flex flex-col gap-2">
                    <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase">Assigned developers</p>
                    {detail.assignments.map((a) => (
                      <div key={a.id} className="flex items-center justify-between font-mono text-[10px]">
                        <span className="text-zinc-300">{a.dev_name || a.dev_email}</span>
                        <button data-testid={`unassign-${a.dev_id}`} onClick={() => unassignDev(a.dev_id)} className="text-zinc-500 hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <select data-testid="assign-dev-select" className={inputCls} value={assignTarget} onChange={(e) => setAssignTarget(e.target.value)}>
                        <option value="">Select developer…</option>
                        {devs.map((d) => <option key={d.id} value={d.id}>{d.name || d.email}</option>)}
                      </select>
                      <button data-testid="assign-dev-button" onClick={assignDev} disabled={!assignTarget} className="shrink-0 px-3 bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-mono text-[9px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors"><UserPlus className="w-3.5 h-3.5" /> Assign</button>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
                {/* Timeline */}
                <div className="border border-white/10 bg-black/40 p-5">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase mb-4">Production Timeline</p>
                  {detail.milestones.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No milestones defined yet.</p>}
                  <MilestoneTimeline milestones={detail.milestones} me={me} isAssignedDev={isAssignedDev} isClient={isClient} onAction={milestoneAction} onDeposit={payDeposit} depositReceived={!!detail.project.deposit_received} />
                </div>

                <div className="flex flex-col gap-5">
                  {/* Asset vault */}
                  <div className="border border-white/10 bg-black/40 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><FolderArchive className="w-3.5 h-3.5" /> Asset Vault ({detail.deliverables.length})</p>
                      {isAssignedDev && (
                        <button data-testid="deliverable-add-toggle" onClick={() => setAddingDeliv((a) => !a)} className="font-mono text-[8px] tracking-[0.15em] uppercase text-nc-blue hover:text-zinc-100 flex items-center gap-1 transition-colors"><Plus className="w-3 h-3" /> Add</button>
                      )}
                    </div>
                    {addingDeliv && (
                      <div data-testid="deliverable-form" className="border border-nc-blue/30 bg-nc-blue/5 p-3 mb-3 flex flex-col gap-2">
                        <input data-testid="deliverable-title" className={inputCls} placeholder="Deliverable title *" value={deliv.title} onChange={(e) => setDeliv((d) => ({ ...d, title: e.target.value }))} />
                        <input data-testid="deliverable-url" className={inputCls} placeholder="File URL (Drive, Dropbox…) *" value={deliv.file_url} onChange={(e) => setDeliv((d) => ({ ...d, file_url: e.target.value }))} />
                        <div className="grid grid-cols-2 gap-2">
                          <input data-testid="deliverable-version" className={inputCls} placeholder="Version" value={deliv.version} onChange={(e) => setDeliv((d) => ({ ...d, version: e.target.value }))} />
                          <input data-testid="deliverable-notes" className={inputCls} placeholder="Notes" value={deliv.notes} onChange={(e) => setDeliv((d) => ({ ...d, notes: e.target.value }))} />
                        </div>
                        <button data-testid="deliverable-submit" onClick={addDeliverable} disabled={!deliv.title.trim() || !deliv.file_url.trim()} className="self-start bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[9px] px-3 py-2 transition-colors">Add to Vault</button>
                      </div>
                    )}
                    {detail.deliverables.length === 0 && <p data-testid="vault-empty" className="font-mono text-[10px] text-zinc-600">Deliverables from the studio will land here.</p>}
                    {detail.deliverables.map((d, i) => (
                      <div key={d.id} data-testid={`deliverable-${i + 1}`} className="flex items-center justify-between gap-3 border-b border-white/5 py-2 font-mono text-[10px]">
                        <div className="min-w-0">
                          <p className="text-zinc-200 truncate">{d.title} <span className="text-zinc-600">v{d.version}</span></p>
                          <p className="text-zinc-600 truncate">{d.uploaded_by} // {String(d.created_at).slice(0, 10)}{d.notes ? ` // ${d.notes}` : ""}</p>
                        </div>
                        {d.file_url && (
                          <a data-testid={`deliverable-link-${i + 1}`} href={d.file_url} target="_blank" rel="noopener noreferrer" className="text-nc-blue hover:text-zinc-100 shrink-0 transition-colors"><ExternalLink className="w-4 h-4" /></a>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Sync chat */}
                  <div className="border border-white/10 bg-black/40 flex flex-col">
                    <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase">Studio Sync Channel</div>
                    <div data-testid="portal-chat-messages" className="overflow-y-auto p-4 flex flex-col gap-2.5 max-h-[300px] min-h-[160px]">
                      {messages.map((m) => {
                        const staff = m.sender_kind === "sales";
                        return (
                          <div key={m.id} className={`max-w-[85%] px-3 py-2 border ${staff ? "self-start bg-white/5 border-white/10" : "self-end bg-nc-blue/10 border-nc-blue/30"}`}>
                            <p className={`font-mono text-[8px] tracking-[0.2em] uppercase mb-1 ${staff ? "text-nc-orange" : "text-nc-blue"}`}>{m.sender_name}</p>
                            <p className="font-mono text-[11px] text-zinc-200 leading-relaxed whitespace-pre-wrap">{m.text}</p>
                          </div>
                        );
                      })}
                      <div ref={bottomRef} />
                    </div>
                    <div className="border-t border-white/10 p-3 flex gap-2">
                      <input data-testid="portal-chat-input" className={inputCls} placeholder="Message the studio…" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
                      <button data-testid="portal-chat-send" onClick={send} disabled={!draft.trim()} className="shrink-0 w-11 bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white flex items-center justify-center transition-colors"><Send className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-white/10 bg-black/40 p-10 text-center">
              <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">Select a project to open its workspace</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
